import React from 'react';
import {Button, Flex, Popover, PopoverPlacement} from '@priority-software/priority-style-react';
import cx from 'classnames';
import {isRTL} from '../../Localization/index';
import {updateBlackList, isAutomation} from './actions/feature';
import {FeatureProp, featureMap, updateLocalBlackList} from './constants/map';
import styles from './featurePopover.module.scss';
import {sendMonitor} from '../../utils/monitoring';
import {getTopDialogZindex} from '../../_shared/GWTBridge/invoke/priModalDlg';
import {setIsNavigationEnabled, returnFocus} from '../../_shared/GWTBridge/invoke/genPage';
import {setHasModalChild} from '../../_shared/GWTBridge/invoke/netForm';
import {formsStore} from '../../_legacy/Form/stores/FormsStore';

export type PopoverContent = {
    title: string | React.ReactNode;
    content: React.ReactNode;
    secondaryButtonText: string;
    primaryButtonText: string;
};

interface FeaturePopoverState extends FeatureProp {
    open: boolean;
    featureId: string;
    anchor: HTMLElement;
    onClosePopover: (isBtnClick?) => void;
    popoverContent: PopoverContent | {};
    ignoreBlackList?: boolean;
    oncePerForm?: boolean;
    isPrimaryCloseButton?: boolean;
    popoverClasses?: string;
}

export class FeaturePopover extends React.Component<{}, FeaturePopoverState> {
    defaultState = {
        open: false,
        title: null,
        content: null,
        visualContent: [],
        primaryButtonText: null,
        confirmButtonHandler: null,
        secondaryButtonText: null,
        anchor: null,
        featureId: null,
        placement: PopoverPlacement.auto,
        onClosePopover: null,
        showMask: false,
        large: false,
        isPrimaryCloseButton: false,
        ignoreBlackList: false,
        clickOutSideClose: false,
        oncePerForm: false,
        closeOnEsc: false,
        forceBlacklistOnClickoutside: false,
        popoverContent: {},
        zIndex: null,
        ignoreLocalBlacklist: false,
        popoverClasses: null
    } as FeaturePopoverState;

    state = {
        ...this.defaultState
    };

    showFeaturePopover = (
        featureId,
        anchor,
        onClosePopover?,
        confirmButtonHandler?,
        popoverContent?: PopoverContent,
        isPrimaryCloseButton?: boolean,
        popoverClasses?: string
    ) => {
        if (!anchor) {
            return;
        }
        const feature: FeatureProp = featureMap.get(featureId);

        if (!isAutomation() && feature && !this.state.open) {
            this.setState({
                open: true,
                anchor,
                featureId,
                onClosePopover,
                isPrimaryCloseButton,
                popoverClasses,
                ...feature,
                ...popoverContent,
                confirmButtonHandler: confirmButtonHandler
                    ? confirmButtonHandler
                    : feature.confirmButtonHandler
            } as FeaturePopoverState);

            sendMonitor(`${featureId} -  display message`);
            if (feature.showMask) {
                setIsNavigationEnabled(false);
                setHasModalChild(true);
            }
        }
    };

    onClose = (isClickOutside?, isBtnClick?) => {
        const {featureId, showMask, open} = this.state;

        if (open) {
            featureId && sendMonitor(`${featureId} -  close message`);
            this.handleClose(isClickOutside);
        }

        !isClickOutside && this.state.onClosePopover && this.state.onClosePopover(isBtnClick);

        if (showMask) {
            setIsNavigationEnabled(true);
            setHasModalChild(false);
        }
    };

    handleClose = (isClickOutside?) => {
        const {
            featureId,
            ignoreBlackList,
            open,
            oncePerForm,
            forceBlacklistOnClickoutside,
            ignoreLocalBlacklist
        } = this.state;

        if (open) {
            if (isClickOutside && !forceBlacklistOnClickoutside) {
                if (oncePerForm) {
                    const {currentForm} = formsStore;
                    currentForm?.setShownFeaturePopovers?.(featureId);
                }
            } else {
                !ignoreBlackList && updateBlackList(featureId);
            }
            !ignoreLocalBlacklist && updateLocalBlackList(featureId);
            this.setState({...this.defaultState} as FeaturePopoverState);
            returnFocus();
        }
    };

    closeById = (featureId: string, asClickOutside: boolean = true) => {
        if (featureId === this.state.featureId) {
            this.onClose(asClickOutside);
        }
    };

    onConfirm = () => {
        this.state.confirmButtonHandler && this.state.confirmButtonHandler();
        this.handleClose();
        sendMonitor(`${this.state.featureId} -  show me more`);
    };
    renderFooter = onHide => {
        const {primaryButtonText, secondaryButtonText, isPrimaryCloseButton} = this.state;
        return (
            <Flex justifyContent="space-between">
                <Button
                    skin={secondaryButtonText ? 'text' : 'primary'}
                    onClick={
                        isPrimaryCloseButton ? () => this.onClose(false, true) : this.onConfirm
                    }
                >
                    {primaryButtonText}
                </Button>
                {secondaryButtonText && (
                    <Button
                        skin="primary"
                        componentClasses={styles.confirmButton}
                        onClick={
                            !isPrimaryCloseButton ? () => this.onClose(false, true) : this.onConfirm
                        }
                    >
                        {secondaryButtonText}
                    </Button>
                )}
            </Flex>
        );
    };
    render() {
        const {
            open,
            anchor,
            content,
            title,
            placement,
            showMask,
            large,
            clickOutSideClose,
            closeOnEsc,
            zIndex,
            popoverClasses
        } = this.state;

        return (
            anchor && (
                <Popover
                    show={open}
                    footer={this.renderFooter}
                    content={content}
                    anchor={anchor}
                    popoverComponentClass={cx('ignoreMenuClickoutside', {
                        [styles.large]: large,
                        [popoverClasses]: !!popoverClasses
                    })}
                    title={title}
                    rtl={isRTL()}
                    strategy="absolute"
                    closable
                    zIndex={zIndex || getTopDialogZindex()}
                    placement={placement}
                    escClosable={showMask || closeOnEsc}
                    hoverable={false}
                    onClose={this.onClose}
                    showMask={showMask}
                    outsideClickClose={clickOutSideClose}
                />
            )
        );
    }
}
